import { markDone } from './state'
import { runOnboarding } from './engine'

const sidebarClickHandlers = new WeakMap()
const submitClickHandlers = new WeakMap()
const modalConfirmClickHandlers = new WeakMap()
const modalConfirmSelector = '.modal.show .modal-footer .btn-success'

function getSidebarMoviesTarget(element) {
  if (!element) return null
  return element.querySelector('a') || element
}

function openMoviesRoute(event) {
  if (event && event.preventDefault) {
    event.preventDefault()
  }
  window.location.href = '/movies'
}

function getSubmitTarget(element) {
  if (!element) return null
  return element.querySelector('button, a') || element
}

function getCountryTarget() {
  return document.querySelector('#contactCountryInput .vs__dropdown-toggle')
    || document.querySelector('#contactCountryInput')
}

function getModalConfirmTarget(element) {
  if (!element) return null
  return element.querySelector('button, a') || element
}

function refreshDriver(context, delay = 0) {
  window.setTimeout(() => {
    if (context && context.driver && context.driver.refresh) {
      context.driver.refresh()
    }
  }, delay)
}

function smoothScroll(context) {
  const activeElement = document.querySelector('.driver-active-element')

  if (activeElement && activeElement.scrollIntoView) {
    activeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    })

    refreshDriver(context, 250)
  }
}

function getModalConfirmButton() {
  return document.querySelector(modalConfirmSelector)
}

function isElementVisible(element) {
  if (!element) return false

  const rect = element.getBoundingClientRect()
  const style = window.getComputedStyle(element)

  return rect.width > 0
    && rect.height > 0
    && style.display !== 'none'
    && style.visibility !== 'hidden'
}

function moveToModalConfirmStep(context) {
  const startedAt = Date.now()
  const timeout = 2500
  const interval = 50

  const tryMoveNext = () => {
    if (isElementVisible(getModalConfirmButton())) {
      window.setTimeout(() => {
        if (!isElementVisible(getModalConfirmButton())) return

        if (context && context.driver && context.driver.moveNext) {
          context.driver.moveNext()
        }

        refreshDriver(context, 100)
        refreshDriver(context, 300)
      }, 250)
      return
    }

    if (Date.now() - startedAt < timeout) {
      window.setTimeout(tryMoveNext, interval)
    }
  }

  tryMoveNext()
}

function moveNextWithScroll(...args) {
  const context = args[2] || {}
  if (context.driver && context.driver.moveNext) {
    context.driver.moveNext()
    window.setTimeout(() => {
      smoothScroll(context)
    }, 150)
  }
}

const movieSidebarSteps = [
  {
    element: '[data-tour="sidebar-movies-menu"]',
    disableActiveInteraction: false,
    onHighlighted: element => {
      const clickTarget = getSidebarMoviesTarget(element)
      if (!clickTarget) return

      sidebarClickHandlers.set(clickTarget, openMoviesRoute)
      clickTarget.addEventListener('click', openMoviesRoute)
    },
    onDeselected: element => {
      const clickTarget = getSidebarMoviesTarget(element)
      if (!clickTarget || !sidebarClickHandlers.has(clickTarget)) return

      clickTarget.removeEventListener('click', sidebarClickHandlers.get(clickTarget))
      sidebarClickHandlers.delete(clickTarget)
    },
    popover: {
      title: 'Movies Menu',
      description: 'Click Movies in the sidebar to continue onboarding.',
      position: 'right',
      showButtons: ['previous'],
    },
  },
]

// eslint-disable-next-line import/prefer-default-export
export const contactFormSteps = [
  {
    element: '#companyNameInput',
    popover: {
      title: 'Company Name',
      description: 'Fill the company name here to proceed with contact creation.',
      side: 'bottom',
      align: 'center',
      onNextClick: moveNextWithScroll,
    },
  },
  {
    element: '#addressInput',
    popover: {
      title: 'Main Address',
      description: 'Enter the main address for the contact.',
      side: 'top',
      align: 'center',
      onNextClick: moveNextWithScroll,
    },
  },
  {
    element: '#cityInput',
    popover: {
      title: 'City',
      description: 'Enter the city for this contact.',
      side: 'top',
      align: 'center',
      onNextClick: moveNextWithScroll,
    },
  },
  {
    element: '#zipCodeInput',
    popover: {
      title: 'Zip Code',
      description: 'Enter the zip code for this contact.',
      side: 'top',
      align: 'center',
      onNextClick: moveNextWithScroll,
    },
  },
  {
    element: '#stateInput',
    popover: {
      title: 'State',
      description: 'Enter the state for this contact.',
      side: 'top',
      align: 'center',
      onNextClick: moveNextWithScroll,
    },
  },
  {
    element: '#contactCountryInput',
    popover: {
      title: 'Country',
      description: 'Select the country for this contact. This is a dropdown, so click to open options and select.',
      side: 'top',
      align: 'center',
      onNextClick: moveNextWithScroll,
    },
  },
  {
    element: () => document.querySelector('[data-tour="contact-form-submit-btn"]'),
    disableActiveInteraction: false,
    onHighlighted: (element, step, context) => {
      const clickTarget = getSubmitTarget(element)
      if (!clickTarget) return

      const clickHandler = () => {
        moveToModalConfirmStep(context)
      }

      submitClickHandlers.set(clickTarget, clickHandler)
      clickTarget.addEventListener('click', clickHandler)

      refreshDriver(context, 0)
      refreshDriver(context, 150)
      refreshDriver(context, 350)
      clickTarget.focus()
    },
    onDeselected: element => {
      const clickTarget = getSubmitTarget(element)
      if (!clickTarget || !submitClickHandlers.has(clickTarget)) return

      clickTarget.removeEventListener('click', submitClickHandlers.get(clickTarget))
      submitClickHandlers.delete(clickTarget)
    },
    popover: {
      title: 'Submit Contact',
      description: 'After filling details, click Submit to open confirmation.',
      side: 'top',
      align: 'center',
      showButtons: ['previous'],
    },
  },
  {
    element: () => getModalConfirmButton(),
    disableActiveInteraction: false,
    onHighlightStarted: (element, step, context) => {
      const clickTarget = getModalConfirmTarget(element)
      if (!clickTarget) return

      const clickHandler = () => {
        markDone('contact-form')

        if (context && context.driver && context.driver.destroy) {
          context.driver.destroy()
        }

        sessionStorage.setItem('onboarding_next', 'movies-list')
        runOnboarding(movieSidebarSteps)
      }

      modalConfirmClickHandlers.set(clickTarget, clickHandler)
      clickTarget.addEventListener('click', clickHandler)
    },
    onHighlighted: (element, step, context) => {
      const clickTarget = getModalConfirmTarget(element)
      if (!clickTarget) return

      refreshDriver(context, 0)
      refreshDriver(context, 150)
      refreshDriver(context, 350)
      clickTarget.focus()
    },
    onDeselected: element => {
      const clickTarget = getModalConfirmTarget(element)
      if (!clickTarget || !modalConfirmClickHandlers.has(clickTarget)) return

      clickTarget.removeEventListener('click', modalConfirmClickHandlers.get(clickTarget))
      modalConfirmClickHandlers.delete(clickTarget)
    },
    popover: {
      title: 'Confirm Submit',
      description: 'Click YES in this confirmation modal to save contact.',
      side: 'top',
      align: 'center',
      showButtons: ['previous'],
    },
  },
]
